import React, { useState, useEffect, useRef, useContext } from "react";
import { useLazyQuery } from "@apollo/react-hooks";
import Queries from "../Queries";
import placeholders from "./../Global/placeholderAutocomplete";
import { GlobalContext } from "./../Context";
import "./PlaceAutocomplete.scss";

// Agregar estos estilos al input para mejorar la interactividad en Safari iOS
const touchableInputStyle = {
  cursor: 'pointer',
  WebkitTapHighlightColor: 'transparent',
  WebkitTouchCallout: 'none',
  WebkitUserSelect: 'none',
  touchAction: 'manipulation'
};

function PlaceAutocomplete(props) {
  const globalContext = useContext(GlobalContext);
  const {
    radius,
    langcode,
    setResultsState,
    setBranches,
    initialPos,
    setInitialPos,
    mapsInstance,
    setMarkersVisibility,
    geolocationAvailable,
    setGeolocationAvailable,
    setFiltersVisibility,
    positionState,
    placeholderText,
    setAdvancedFiltersVisibility,
    setPlaceholderText,
    ctaLink,
    activeFilters,
    setBackButtonVisibility
  } = globalContext;

  let tids = activeFilters ? activeFilters.join("+") : "";
  const [currentMarkers, setCurrentMarkers] = useState([]);
  const [isButtonClicked, setIsButtonClicked] = useState("");
  const [queryState, setQueryState] = useState("waiting");
  const placeHolderInput = useRef(null);
  const placeHolderInputClone = useRef(null);
  const [isChecked, setIsChecked] = useState(false);
  const [proximityValue, setProximityValue] = useState("");
  const [autoCompleteHasContent, setAutoCompleteHasContent] = useState(false);
  const [geolocationState, setGeolocationState] = useState("");
  const [startAutocomplete, setStartAutocomplete] = useState(false);
  const [inputEnterKeyPressed, setInputEnterKeyPressed] = useState(false);
  const [getBranches, { loading, data }] = useLazyQuery(Queries("branches"), {
    variables: {
      langcode: langcode,
      tids: tids,
      field_coordinates_proximity: proximityValue,
    },
  });

  const setButtonState = (isClicked) => {
    setIsButtonClicked(isClicked);
  };

  const triggerEvent = (el, type, keyCode) => {
    if ("createEvent" in document) {
      // modern browsers, IE9+
      let event = document.createEvent("HTMLEvents");
      event.keyCode = keyCode;
      event.initEvent(type, false, true);
      el.dispatchEvent(event);
    } else {
      // IE 8
      let event = document.createEventObject();
      event.keyCode = keyCode;
      event.eventType = type;
      el.fireEvent("on" + event.eventType, event);
    }
  };

  // Sets the proximity for the default query value.
  useEffect(() => {
    if (
      Object.keys(initialPos).length !== 0 &&
      initialPos.constructor === Object
    ) {
      let lat = initialPos.lat;
      let lng = initialPos.lng;
      let tempProximityValue = `${lat},${lng}<${radius / 1000}km`;
      setProximityValue(tempProximityValue);
    }
  }, [initialPos, radius]);

  useEffect(() => {
    if (data && mapsInstance && isButtonClicked) {
      let { google } = mapsInstance;
      let queryCircle = new google.maps.Circle({
        strokeColor: "#FF0000",
        strokeOpacity: 0.8,
        strokeWeight: 2,
        center: new google.maps.LatLng(initialPos),
        radius: radius,
      });
      let markers = [];
      data.officeBranchesQuery.results.forEach((item) => {
        let location = new google.maps.LatLng({
          lat: item.fieldCoordinates.lat,
          lng: item.fieldCoordinates.lng,
        });
        let marker = new google.maps.Marker({
          position: location,
        });
        if (
          google.maps.geometry.spherical.computeDistanceBetween(
            marker.getPosition(),
            queryCircle.getCenter()
          ) <= queryCircle.getRadius()
        ) {
          markers.push(item);
        }
      });

      if (markers.length > 0) {
        setQueryState("success");
        setCurrentMarkers(markers);
      } else {
        setQueryState("fails");
      }
      setButtonState(false);
    }
  }, [data, mapsInstance, isButtonClicked, initialPos, radius]);

  useEffect(() => {
    if (currentMarkers.length > 0) {
      setBackButtonVisibility(true);
      setBranches(currentMarkers);
      setFiltersVisibility(false);
      setAdvancedFiltersVisibility(false);
      setResultsState(true);
      setMarkersVisibility(true);
    }
  }, [
    currentMarkers,
    setBranches,
    setResultsState,
    setMarkersVisibility,
    setFiltersVisibility,
    setAdvancedFiltersVisibility,
    setBackButtonVisibility
  ]);

  useEffect(() => {
    if (
      Object.keys(mapsInstance).length !== 0 &&
      mapsInstance.constructor === Object &&
      geolocationState !== "loading" &&
      (startAutocomplete || inputEnterKeyPressed)
    ) {
      let input = placeHolderInput.current;
      let googleMaps = mapsInstance.google;
      let maps = mapsInstance.maps;
      let autoComplete = new googleMaps.maps.places.Autocomplete(input, {
        fields: ["formatted_address", "geometry"],
      });
      let currentLaguageCode = langcode.replace("es-", "");
      autoComplete.setComponentRestrictions({ country: [currentLaguageCode] });

      autoComplete.addListener("place_changed", function () {
        var place = autoComplete.getPlace();

        if (!place.formatted_address) {
          // Triggers keydown event, executing arrow down and then enter, in order to select the first position
          triggerEvent(input, "keydown", 40);
          triggerEvent(input, "keydown", 13);
        }

        if (place.geometry) {
          if (place.geometry.viewport) {
            setPlaceholderText(place.formatted_address);
            maps.fitBounds(place.geometry.viewport);

            if (window.innerWidth < 767) {
              maps.setZoom(14);
            } else {
              maps.setZoom(15);
            }
          } else {
            maps.setCenter(place.geometry.location);

            if (window.innerWidth < 767) {
              maps.setZoom(14);
            } else {
              maps.setZoom(15);
            }
          }

          setInitialPos({
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
          });
        }
      });

      if (inputEnterKeyPressed) {
        setTimeout(() => {
          triggerEvent(input, "keydown", 40);
        }, 500);
      }
    }
  }, [
    mapsInstance,
    langcode,
    setInitialPos,
    geolocationState,
    setPlaceholderText,
    startAutocomplete,
    inputEnterKeyPressed,
  ]);

  useEffect(() => {
    setIsChecked(geolocationAvailable);
  }, [geolocationAvailable]);

  useEffect(() => {
    if (startAutocomplete || inputEnterKeyPressed) {
      placeHolderInput.current.focus();
    } else {
      placeHolderInputClone.current.focus();
    }
  }, [startAutocomplete, inputEnterKeyPressed]);

  // Agregar manejadores de eventos táctiles
  const handleTouchEnd = (e) => {
    e.preventDefault();
    e.target.click();
    e.target.focus();

    if(e.target.checked) {
      e.target.checked = !e.target.checked;
    }
  };

  return (
    <div className="place-search">
      <h4 className="place-search__title">Ingrese una ubicación</h4>
      {startAutocomplete || inputEnterKeyPressed ? (
        <input
          className="place-search__input"
          type="text"
          style={touchableInputStyle}
          onTouchEnd={handleTouchEnd}
          placeholder={placeholders(langcode).name}
          ref={placeHolderInput}
          disabled={
            geolocationState === "loading" || positionState === "loading"
              ? true
              : false
          }
          onClick={() => {
            if (geolocationState === "loading") return;
            setIsChecked(false);
          }}
          onChange={(e) => {
            if (e.target.value.length <= 5) {
              setStartAutocomplete(false);
            }

            setPlaceholderText(e.target.value);

            if (e.target.value !== "") {
              setAutoCompleteHasContent(true);
            } else {
              setAutoCompleteHasContent(false);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              const dropdown = document.querySelectorAll(
                ".pac-container.pac-logo.hdpi"
              );
              dropdown.forEach((element) => {
                if (element) element.style.display = "none";
              });
              e.preventDefault();
            }
            if (e.key === "Backspace") {
              setInputEnterKeyPressed(false);
            }
          }}
          value={placeholderText}
        />
      ) : null}
      {!startAutocomplete && !inputEnterKeyPressed ? (
        <input
          className="place-search__input"
          type="text"
          style={touchableInputStyle}
          onTouchEnd={handleTouchEnd}
          placeholder={placeholders(langcode).name}
          ref={placeHolderInputClone}
          disabled={
            geolocationState === "loading" || positionState === "loading"
              ? true
              : false
          }
          onClick={() => {
            if (geolocationState === "loading") return;
            setIsChecked(false);
          }}
          onChange={(e) => {
            if (e.target.value.length > 4) {
              setStartAutocomplete(true);
            }
            setPlaceholderText(e.target.value);

            if (e.target.value !== "") {
              setAutoCompleteHasContent(true);
            } else {
              setAutoCompleteHasContent(false);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              const dropdown = document.querySelectorAll(
                ".pac-container.pac-logo.hdpi"
              );
              dropdown.forEach((element) => {
                if (element) element.style.display = "none";
              });
              e.preventDefault();
              setInputEnterKeyPressed(true);
            }
          }}
          value={placeholderText}
        />
      ) : null}
      <div className="checkbox-wrapper">
        <input
          id="placeSearchCurrentLocation"
          style={touchableInputStyle}
          onTouchEnd={handleTouchEnd}
          aria-labelledby="ubicación actual"
          className="place-search__checkbox"
          type="checkbox"
          name="current-location"
          value="current-location"
          checked={geolocationAvailable && isChecked ? true : false}
          disabled={geolocationAvailable ? false : true}
          onChange={() => {
            if (geolocationState === "loading") return;
            setIsChecked(!isChecked);
            if (!isChecked) {
              setGeolocationState("loading");
              navigator.geolocation.getAccurateCurrentPosition(
                (position) => {
                  if (position) {
                    placeHolderInput.current.value = "";
                    setGeolocationState("success");
                    setInitialPos({
                      lat: position.coords.latitude,
                      lng: position.coords.longitude,
                    });
                  }
                },
                () => {
                  setGeolocationState("fails");
                  setGeolocationAvailable(false);
                },
                () => {},
                { desiredAccuracy: 50, maxWait: 10000 }
              );
            }
          }}
        />
        <label
          className="place-search__label"
          htmlFor="placeSearchCurrentLocation"
          onTouchEnd={handleTouchEnd}
        >
          <p>Utilizar mi ubicación actual</p>
        </label>
      </div>
      {!geolocationAvailable && !autoCompleteHasContent ? (
        <span className="place-search__input-error">
          No hemos logrado obtener su ubicación, por favor escriba una
          dirección.
        </span>
      ) : null}
      {geolocationAvailable && !isChecked && !autoCompleteHasContent ? (
        <span className="place-search__input-error">
          Por favor ingrese una ubicación manualmente.
        </span>
      ) : null}
      <div className="place-search__ctas">
        <button
          className={`place-search__submit ${
            loading ? "place-search__submit--loading" : ""
          } ${
            positionState === "loading" || geolocationState === "loading"
              ? "place-search__submit--disabled"
              : ""
          }`}
          style={touchableInputStyle}
          onTouchEnd={handleTouchEnd}
          onClick={(e) => {
            e.preventDefault();
            if (geolocationState === "loading") return;
            if (positionState === "success" || positionState === "failed") {
              setButtonState(true);
              getBranches();
            }
          }}
        >
          Buscar
        </button>
      </div>
      {positionState === "loading" || geolocationState === "loading" ? (
        <span className="place-search__submit-help-text place-search__submit-help-text--loading">
          Obteniendo ubicación, por favor espere
        </span>
      ) : null}
      {!(queryState === "waiting" || queryState === "success") && (
        <p className="place-search__submit-help-text place-search__submit-help-text--error">
          No se han podido encontrar resultados, por favor modifique su búsqueda
          o ingrese una ubicación diferente.
        </p>
      )}
      {ctaLink && (
        <>
          <div className="place-search__divider" />
          <div className="place-search-cta-container">
            <h5>Agende su espacio</h5>
            <div className="place-search-cta-list">
              {ctaLink && ctaLink.url && ctaLink.title && (
                <div className="place-search__cta">
                  <img
                    className="place-search__cta-image"
                    src="/modules/custom/bac_locations/js/src/assets/img/ticket.svg"
                    alt="Filter ticket"
                  />
                  <a
                    className="place-search__cta-label"
                    target="_blank"
                    rel="noopener noreferrer"
                    href={ctaLink.url.path}
                    onTouchEnd={handleTouchEnd}
                  >
                    {ctaLink.title}
                  </a>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default PlaceAutocomplete;
